import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Which edges of a sideways-scrolling strip are hiding something.
 *
 *  A strip that scrolls on purpose says so with a fade on the edge that
 *  hides more, and declares it to the layout lint with
 *  `data-overflow="shown"` (the SHA ready round, D25): the period picker,
 *  the money desks' view strips, the workspace tab strip and the stock
 *  shelf switcher all read the same measurement. */
export function useScrollEdges<T extends HTMLElement>(deps: ReadonlyArray<unknown> = []): {
  ref: RefObject<T>;
  edges: { start: boolean; end: boolean };
  overflow: 'shown' | undefined;
  measure: () => void;
} {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const next = { start: el.scrollLeft > 2, end: max > 2 && el.scrollLeft < max - 2 };
    setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(measure, [measure, ...deps]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure]);
  return { ref, edges, overflow: edges.start || edges.end ? 'shown' : undefined, measure };
}

/** The classes a strip wears for its fades, from the hook's edges. */
export function scrollEdgeClasses(edges: { start: boolean; end: boolean }): string {
  return [edges.start ? 'mrd-strip--more-start' : '', edges.end ? 'mrd-strip--more-end' : ''].filter(Boolean).join(' ');
}
